import { AlertColor, Autocomplete, Badge, Box, CircularProgress, ClickAwayListener, Grid, IconButton, Popper, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { Dispatch, useImperativeHandle, SetStateAction, useContext, useEffect, useState, forwardRef, ForwardedRef } from "react";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useBase64 } from "../../hooks/useBase64";
import GenericPromises from "../../api/GenericPromises";
import { myStylePickers } from "../../theme/buttons";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useGridPreferences } from "../../hooks/useGridPreferences";
import { useGridFilterPreferencesStore, usePreferenceTypesStore } from "../../store/Preferences/PreferencesStore";
import { AuthContext } from "../../context/AuthContext";
import { useMenuStore } from "../../store/AuthStore";
import { PreferenceType } from "../../interfaces/Commons/Preferences/preferences";
import { FilterValueInput } from "./valueColumn";

interface MyComponentProps {
    setIsLoadingDataTable: Dispatch<SetStateAction<boolean>>,
    setDataTable: Dispatch<SetStateAction<readonly GridValidRowModel[]>>,
    showSnackBar: (message: string, severity: AlertColor) => void,
    columnsTable: GridColDef<any>[],
    initFilter: string,
    resourceRoute: string,
    isLoading?: boolean,
    onApplyFilter?: ((filter: any) => void) | null,
    defaultFilter?: Filter[],
    onFilterEmpty?: (() => void) | null,
}

export interface Filter {
    idFilter: number;
    column: string;
    operator: string;
    value: string;
}

export interface FilterTableRef {
    getFilters: () => Filter[];
    applyFilter: () => void;
    getDataByRoute: (route: string) => any;
}

export const FilterTable = forwardRef(
    (
        { setIsLoadingDataTable,
            setDataTable,
            showSnackBar,
            columnsTable,
            initFilter,
            resourceRoute,
            isLoading,
            onApplyFilter,
            defaultFilter,
            onFilterEmpty }: MyComponentProps,
        ref: ForwardedRef<FilterTableRef>
    ) => {
        const [t] = useTranslation("global");
        const { utf8ToBase64Url } = useBase64();
        const { GenericGetResource } = GenericPromises();
        const [openDatePickerIndex, setOpenDatePickerIndex] = useState<number | null>(null);
        const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
        const { findResourceIdByRoute } = useGridPreferences();
        const { getColumnFilterPreferenceByResourceId, updateColumnFilterPreference } = useGridFilterPreferencesStore();
        const { getPreferenceTypeIdByPreferenceName } = usePreferenceTypesStore();
        const { GenericPostResource, GenericPutResource } = GenericPromises();
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const { user } = useContext(AuthContext);
        const { menuState } = useMenuStore();
        const { preferenceTypes } = usePreferenceTypesStore.getState();
        const { LoadFilter, VerifyPreferenceFilterByResourceRoute } = useGridPreferences();
        const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
        const [typeValue, setTypeValue] = useState<{ index: number, type: string }[]>([]);

        const [comboFilters, setComboFilters] = useState<Filter[]>(defaultFilter ?? []);

        const operatorArray = [
            { operator_id: 1, option_name: t('generic.operators.equals'), value: '=' },
            { operator_id: 2, option_name: t('generic.operators.not-equals'), value: '!=' },
            { operator_id: 3, option_name: t('generic.operators.greater'), value: '>' },
            { operator_id: 4, option_name: t('generic.operators.less'), value: '<' },
            { operator_id: 5, option_name: t('generic.operators.greater_equal'), value: '>=' },
            { operator_id: 6, option_name: t('generic.operators.less_equal'), value: '<=' },
            { operator_id: 7, option_name: t('generic.operators.include'), value: 'LIKE' },
        ];

        const open = Boolean(anchorEl);
        const id = open ? 'simple-popper' : undefined;

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            if (isLoadedMyLocalPreferences) {
                setAnchorEl(anchorEl ? null : event.currentTarget);
            }
        };

        useImperativeHandle(ref, () => ({
            getFilters: () => comboFilters,
            applyFilter: () => ApplyFilter(comboFilters),
            getDataByRoute: (route: string) => getDataByRoute(route),
        }));

        const getDataByRoute = async (route: string) => {
            try {
                const response = await GenericGetResource(route);
                if (response && response.data.data) {
                    return response.data.data;
                } else {
                    return [];
                }
            } catch (error) {
                showSnackBar(t("generic.snackbar.error"), "error");
                return [];
            }
        };

        const filterPreferences = async () => {
            let myPreferencesFilter = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridFilters")
            if (myPreferencesFilter) {
                try {
                    const preference = await VerifyPreferenceFilterByResourceRoute(
                        resourceRoute,
                        myPreferencesFilter.preference_type_id ?? 0
                    );
                    if (preference) {
                        let myLocalPreferences = await LoadFilter(resourceRoute, preference);
                        if (myLocalPreferences.length > 0) {
                            setComboFilters(myLocalPreferences);
                            setIsLoadingDataTable(true);
                            ApplyFilter(myLocalPreferences);
                        }
                        else if (defaultFilter && defaultFilter.some((item) => item.column !== "")) {
                            setIsLoadingDataTable(true);
                            ApplyFilter(defaultFilter);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching column visual preferences:", error);
                }
            }
        }


        const handleValidation = (): boolean => {
            const newErrors: { [key: string]: boolean } = {};
            let isValid = true;

            comboFilters.forEach((filter, index) => {
                if (!filter.column) {
                    newErrors[`column-${index}`] = true;
                    isValid = false;
                }
                if (!filter.operator) {
                    newErrors[`operator-${index}`] = true;
                    isValid = false;
                }
            });

            setErrors(newErrors);
            return isValid;
        };

        const handleDeleteAll = () => {
            setComboFilters([])
        };

        const disableKeyboardEntry = (e: any) => {
            if (e?.preventDefault) {
                e?.preventDefault();
                e?.stopPropagation();
            }
        };

        const handleAddFilter = () => {
            if (handleValidation()) {
                const newFilter = {
                    idFilter: comboFilters.length + 1,
                    column: "",
                    operator: "",
                    value: ""
                };
                setComboFilters([...comboFilters, newFilter]);
            }
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const handleApplyFilter = async () => {
            if (handleValidation()) {
                setIsLoadingDataTable(true);
                setDataTable([]);
                SaveFilter();
                ApplyFilter(comboFilters);
            }
        };

        const ApplyFilter = async (filters: Filter[]) => {
            let filterString = "";
            filters.forEach((filter, index) => {
                if (index > 0) {
                    filterString += "&";
                }
                filterString += `${filter.column}${filter.operator}${(filter.value.trim() !== "") ? `${filter.value.trim()}` : "NULL"}`;
            });
            if (filterString !== "") {
                await GenericGetResource(`${initFilter}${utf8ToBase64Url(filterString)}`).then((response) => {
                    if (response && response.data.data) {
                        if (onApplyFilter) {
                            onApplyFilter(response.data.data);
                        }
                        else {
                            setDataTable(response.data.data);
                            setIsLoadingDataTable(false);
                        }
                    }
                    else {
                        setDataTable([])
                        setIsLoadingDataTable(false);
                    }
                }).catch((error) => {
                    setIsLoadingDataTable(false);
                    showSnackBar(t("generic.snackbar.error"), "error");
                });
            } else {
                if (onFilterEmpty) {
                    onFilterEmpty();
                }
                setIsLoadingDataTable(false);
            }
        }

        const SaveFilter = async () => {
            // get resource_id by route - validate if has id(put) if not post
            let myResourceId = findResourceIdByRoute(menuState, resourceRoute);
            // validate order preference get id from state
            let myPreference = getColumnFilterPreferenceByResourceId(myResourceId ?? 0);
            // get preferenceTypeId by Name
            let myPreferenceTypeId = getPreferenceTypeIdByPreferenceName("GridFilters");
            // translate preferences values to backend like PreferenceOrderValue
            let myPreferenceValue = comboFilters.map((element) => {
                let preference = {
                    column: element.column,
                    operator: element.operator,
                    value: element.value
                }
                return preference;
            });
            if (myPreference != undefined && myPreference.preference_id) {
                let myData = {
                    preference_value: JSON.stringify(myPreferenceValue),
                }
                // put
                GenericPutResource(`/preferences/${myPreference.preference_id}`, myData)
                    .then((responsePut) => {
                        // update state
                        let myObject = responsePut.data;
                        updateColumnFilterPreference(myObject);
                    })
                    .catch((error) => { throw error; });
            }
            else {
                let myData = {
                    preference_value: JSON.stringify(myPreferenceValue),
                    resource_id: myResourceId,
                    user_company_id: user?.user_company_id,
                    preference_type_id: myPreferenceTypeId,
                }
                // post
                GenericPostResource(`/preferences`, myData)
                    .then((responsePost) => {
                        // update state
                        let myObject = responsePost.data;
                        updateColumnFilterPreference(myObject);
                    })
                    .catch((error) => { throw error; });
            }
        }

        useEffect(() => {
            const loadData = async () => {
                await filterPreferences();
                setIsLoadedMyLocalPreferences(true);
            }
            loadData();
        }, [])

        useEffect(() => {
            if (comboFilters.length > 0 && columnsTable.length > 0) {
                comboFilters.map((filter: Filter, index: number) => {
                    const myColumn = columnsTable.find((col) => col.field === filter.column);
                    const newType = myColumn?.type ?? "";
                    setTypeValue((prev) => [...prev.filter(t => t.index !== index), { index, type: newType }]);
                })
            }
        }, [columnsTable, comboFilters]);


        const onChangeColumn = (filter: Filter, index: number) => {
            const myColumn = columnsTable.find((col) => col.field === filter.column);
            const newType = myColumn?.type ?? "";
            const validOperators = getValidOperators(newType);
            const currentOperator = comboFilters[index].operator;

            // Si el operador actual no es válido, lo eliminamos
            if (!validOperators.some(op => op.value === currentOperator)) {
                comboFilters[index].operator = "";
            }
            comboFilters[index].value = "";

            setTypeValue((prev) => [...prev.filter(t => t.index !== index), { index, type: newType }]);
        };

        const getValidOperators = (type: string) => {
            switch (type) {
                case "number":
                case "date":
                    return operatorArray.filter(op => ["=", "!=", ">", "<", ">=", "<="].includes(op.value));
                case "boolean":
                    return operatorArray.filter(op => ["=", "!="].includes(op.value));
                default:
                    return operatorArray.filter(op => ["=", "!=", "LIKE"].includes(op.value));
            }
        };


        return (
            <>
                <ClickAwayListener onClickAway={handleClose}>
                    <Box>
                        <Typography
                            sx={{
                                display: "flex",
                                cursor: "pointer",
                                padding: 1,
                                borderRadius: 1,
                                ":hover": {
                                    backgroundColor: '#e0e0e0',
                                }
                            }}
                            onClick={handleClick}
                            fontWeight={"Bold"}
                        >
                            {t("filter.title")}
                            <Badge badgeContent={(isLoadedMyLocalPreferences) ?
                                comboFilters.filter((item) => item.column !== "").length
                                :
                                <CircularProgress size={'1rem'} />
                            }
                                color="secondary">
                                <FilterAltIcon />
                            </Badge>
                        </Typography>
                        <Popper
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            sx={{
                                backgroundColor: 'white',
                                boxShadow: 5,
                                zIndex: 1000,
                                width: 600,
                                borderRadius: 1
                            }}
                            placement="bottom-end"
                        >
                            <Grid container spacing={1} sx={{ padding: 1, borderBottom: '1px solid #e0e0e0' }}>
                                <Grid item>
                                    <Tooltip title={t("filter.buttons.delete_all_filter")}>
                                        <IconButton onClick={handleDeleteAll} disabled={isLoading} color={'error'}><FilterAltOffIcon /></IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item sx={{ flexGrow: 1 }} />
                                <Grid item>
                                    <Tooltip title={t("filter.buttons.add_filter")}>
                                        <IconButton onClick={handleAddFilter} disabled={isLoading} sx={{ color: 'rgb(120, 118, 231)' }}><AddCircleOutlineIcon /></IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Tooltip title={t("filter.buttons.apply_filter")}>
                                        <IconButton onClick={handleApplyFilter} disabled={isLoading} color={'success'}><CheckCircleOutlineIcon /></IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            {isLoading ?
                                <>
                                    <Box
                                        className="d-flex flex-column"
                                        sx={{
                                            minHeight: `${comboFilters.length * 5}vh`,
                                            padding: 1,
                                            '&::-webkit-scrollbar': {
                                                width: '0px',
                                            },
                                        }}
                                    >
                                        {[...Array(comboFilters.length)].map((_, index) => (
                                            <Skeleton
                                                key={index}
                                                variant="rounded"
                                                sx={{
                                                    minHeight: '5vh',
                                                    minWidth: '100%',
                                                    marginTop: 1
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </>
                                :
                                <>
                                    <Box
                                        sx={{
                                            maxHeight: '50vh',
                                            overflowY: 'auto',
                                            padding: 1,
                                            paddingRight: 0.5,
                                            '&::-webkit-scrollbar': {
                                                width: '0px',
                                            },
                                        }}
                                    >
                                        {comboFilters.length === 0 && <Box className="d-flex justify-content-center">
                                            <Typography>{t("filter.info.no_filter")}</Typography>
                                        </Box>}
                                        {comboFilters.map((filter, index) => (
                                            <Box key={Number(filter.idFilter)} className="w-100 pt-1 d-flex flex-row">
                                                <Box sx={{ width: '40%' }}>
                                                    <Autocomplete
                                                        size="small"
                                                        options={columnsTable}
                                                        defaultValue={columnsTable.find((element: any) => comboFilters[index].column === element["field"])}
                                                        getOptionLabel={(option) => option["headerName"] ?? ""}
                                                        renderOption={(props, option) => (
                                                            <div key={option["field"] ?? ""}>
                                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                    {option["headerName"] ?? ""}
                                                                </Box>
                                                            </div>
                                                        )}
                                                        sx={{ width: "100%", paddingRight: 1 }}
                                                        isOptionEqualToValue={(option, value) => option["field"] === value["field"]}
                                                        onChange={(_, value) => {
                                                            const updatedFilters = [...comboFilters];
                                                            updatedFilters[index].column = (value) ? value["field"] ?? "" : "";
                                                            setComboFilters(updatedFilters);
                                                            onChangeColumn(updatedFilters[index], index);
                                                            setErrors({ ...errors, [`column-${index}`]: false });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t("filter.fields.column")}
                                                                variant="filled"
                                                                error={errors[`column-${index}`]}
                                                                helperText={errors[`column-${index}`] ? t("generic.forms-errors.required") : ""}
                                                            />
                                                        )}
                                                        clearOnEscape={false}
                                                        clearIcon={null}
                                                    />
                                                </Box>
                                                <Box sx={{ width: '35%' }}>
                                                    <Autocomplete
                                                        size="small"
                                                        options={getValidOperators(typeValue.find(t => t.index === index)?.type || "")}
                                                        defaultValue={operatorArray.find((element: any) => comboFilters[index].operator === element.value)}
                                                        getOptionLabel={(option) => option.option_name}
                                                        renderOption={(props, option) => (
                                                            <div key={option.operator_id}>
                                                                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                    {option.option_name}
                                                                </Box>
                                                            </div>
                                                        )}
                                                        sx={{ width: "100%", paddingRight: 1 }}
                                                        isOptionEqualToValue={(option, value) => option.operator_id === value.operator_id}
                                                        onChange={(_, value) => {
                                                            const updatedFilters = [...comboFilters];
                                                            updatedFilters[index].operator = value?.value ?? "";
                                                            setComboFilters(updatedFilters);
                                                            setErrors({ ...errors, [`operator-${index}`]: false });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t("filter.fields.operator")}
                                                                variant="filled"
                                                                error={errors[`operator-${index}`]}
                                                                helperText={errors[`operator-${index}`] ? t("generic.forms-errors.required") : ""}
                                                            />
                                                        )}
                                                        clearOnEscape={false}
                                                        clearIcon={null}
                                                    />
                                                </Box>
                                                <Box sx={{ width: '40%' }}>
                                                    <FilterValueInput
                                                        type={columnsTable.find(col => col.field === filter.column)?.type || ""}
                                                        value={comboFilters[index].value}
                                                        index={index}
                                                        setComboFilters={setComboFilters}
                                                        comboFilters={comboFilters}
                                                        openDatePickerIndex={openDatePickerIndex}
                                                        setOpenDatePickerIndex={setOpenDatePickerIndex}
                                                        myStylePickers={myStylePickers}
                                                        disableKeyboardEntry={disableKeyboardEntry}
                                                        t={t}
                                                    />
                                                </Box>
                                                <Box className="w-auto d-flex" sx={{ alignItems: 'flex-start' }}>
                                                    {comboFilters.length > 1 &&
                                                        <IconButton aria-label="delete" onClick={() => {
                                                            const updatedFilters = comboFilters.filter((_, i) => i !== index);
                                                            setComboFilters(updatedFilters);
                                                        }}>
                                                            <DeleteIcon />
                                                        </IconButton>}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            }
                        </Popper>
                    </Box>
                </ClickAwayListener>
            </>
        )
    })